import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { getTeacherClasses } from '../../redux/slices/lessonsSlice';
import { getTeacherSubjects } from '../../redux/slices/subjectsSlice';
import { toast } from 'react-toastify';
import './AddLessonModal.css';

function EditLessonModal({ isOpen, onClose, lesson, onSuccess }) {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.auth);
  const { teacherClasses, teacherClassesLoading, teacherClassesError } = useSelector((state) => state.lessons);
  const { teacherSubjects, subjectsLoading } = useSelector((state) => state.subjects);

  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    description_ar: '',
    description_en: '',
    class_id: '',
    subject_id: '',
    day: '',
    time: '',
    video_link: '',
    video_type: 'offline',
    image: null,
    features: [{ title_ar: '', title_en: '' }],
    files: [{ file: null, title_ar: '', title_en: '' }]
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // جلب مواد المعلم عند فتح المودل
  useEffect(() => {
    if (isOpen) {
      dispatch(getTeacherClasses());
      dispatch(getTeacherSubjects());
    }
  }, [isOpen, dispatch]);

  // تحديث البيانات عند تغيير الدرس
  useEffect(() => {
    if (lesson && isOpen) {
      // التأكد من أن features و files مصفوفات
      const lessonFeatures = Array.isArray(lesson.features) ? lesson.features : [];
      const lessonFiles = Array.isArray(lesson.files) ? lesson.files : [];
      
             setFormData({
         title_ar: lesson.title_ar || lesson.name || '',
         title_en: lesson.title_en || '',
         description_ar: lesson.description_ar || '',
         description_en: lesson.description_en || '',
         class_id: lesson.class_id || lesson.class?.id || '',
         subject_id: lesson.subject_id || lesson.subject?.id || '',
         day: lesson.day || '',
         time: lesson.time || '',
         video_link: lesson.video_link || '',
         video_type: lesson.video_type || 'offline',
         image: null,
         features: lessonFeatures.length > 0 ? lessonFeatures : [{ title_ar: '', title_en: '' }],
         files: lessonFiles.length > 0 ? lessonFiles : [{ file: null, title_ar: '', title_en: '' }]
       });

      if (lesson.image) {
        setPreviewImage(lesson.image);
      }
    }
  }, [lesson, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: Array.isArray(prev.features) ? [...prev.features, { title_ar: '', title_en: '' }] : [{ title_ar: '', title_en: '' }]
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: Array.isArray(prev.features) ? prev.features.filter((_, i) => i !== index) : [{ title_ar: '', title_en: '' }]
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      features: Array.isArray(prev.features) ? prev.features.map((feature, i) => 
        i === index ? { ...feature, [field]: value } : feature
      ) : [{ title_ar: '', title_en: '' }]
    }));
  };

  const addFile = () => {
    setFormData(prev => ({
      ...prev,
      files: Array.isArray(prev.files) ? [...prev.files, { file: null, title_ar: '', title_en: '' }] : [{ file: null, title_ar: '', title_en: '' }]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: Array.isArray(prev.files) ? prev.files.filter((_, i) => i !== index) : [{ file: null, title_ar: '', title_en: '' }]
    }));
  };

  const handleFileTitleChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      files: Array.isArray(prev.files) ? prev.files.map((fileItem, i) => 
        i === index ? { ...fileItem, [field]: value } : fileItem
      ) : [{ file: null, title_ar: '', title_en: '' }]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!lesson?.id) {
      toast.error('معرف الدرس مطلوب');
      return;
    }

    setIsUpdating(true);

    try {
      const token = localStorage.getItem("token3almny");
      if (!token) {
        toast.error("يجب تسجيل الدخول أولاً");
        return;
      }

      const formDataToSend = new FormData();
      
      // إضافة البيانات الأساسية
      formDataToSend.append('title_ar', formData.title_ar);
      formDataToSend.append('title_en', formData.title_en);
      formDataToSend.append('description_ar', formData.description_ar);
      formDataToSend.append('description_en', formData.description_en);
      formDataToSend.append('class_id', formData.class_id);
      formDataToSend.append('subject_id', formData.subject_id);
             formDataToSend.append('day', formData.day);
       formDataToSend.append('time', formData.time);
       formDataToSend.append('video_type', formData.video_type);
       formDataToSend.append('video_link', formData.video_link);

      // إضافة الصورة إذا تم تغييرها
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      // إضافة المميزات
      if (Array.isArray(formData.features)) {
        formData.features.forEach((feature, index) => {
          if (feature && feature.title_ar && feature.title_ar.trim() || feature.title_en && feature.title_en.trim()) {
            formDataToSend.append(`features[${index}][title_ar]`, feature.title_ar || '');
            formDataToSend.append(`features[${index}][title_en]`, feature.title_en || '');
          }
        });
      }

      // إضافة الملفات
      if (Array.isArray(formData.files)) {
        formData.files.forEach((fileItem, index) => {
          if (fileItem && fileItem.file) {
            formDataToSend.append(`files[${index}][file]`, fileItem.file);
          }
          if (fileItem && (fileItem.title_ar && fileItem.title_ar.trim() || fileItem.title_en && fileItem.title_en.trim())) {
            formDataToSend.append(`files[${index}][title_ar]`, fileItem.title_ar || '');
            formDataToSend.append(`files[${index}][title_en]`, fileItem.title_en || '');
          }
        });
      }

      const response = await fetch(`https://newalemny.3almny.app/api/lessons/update_one/${lesson.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('تم تحديث الدرس بنجاح');
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        toast.error(data.message || 'فشل في تحديث الدرس');
      }
    } catch (error) {
      console.error('Error updating lesson:', error);
      toast.error('حدث خطأ في الاتصال بالخادم');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay ${isDarkMode ? 'dark' : ''}`}>
      <div className="modal-content edit-lesson-modal " style={{transform: 'translateY(5px)'}}>
        <div className="modal-header">
          <h3>تعديل الدرس</h3>
          <button className="close-btn" onClick={handleClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h4>معلومات الدرس الأساسية</h4>
            <div className="row">
              <div className="form-group col-6">
                <label>عنوان الدرس (عربي)</label>
                <input
                  type="text"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الدرس بالعربية"
                  required
                />
              </div>
              <div className="form-group col-6">
                <label>عنوان الدرس (إنجليزي)</label>
                <input
                  type="text"
                  name="title_en"
                  value={formData.title_en}
                  onChange={handleInputChange}
                  placeholder="أدخل عنوان الدرس بالإنجليزية"
                  required
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-6">
                <label>وصف الدرس (عربي)</label>
                <textarea
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleInputChange}
                  placeholder="أدخل وصف الدرس بالعربية"
                  rows="3"
                />
              </div>
              <div className="form-group col-6">
                <label>وصف الدرس (إنجليزي)</label>
                <textarea
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleInputChange}
                  placeholder="أدخل وصف الدرس بالإنجليزية"
                  rows="3"
                />
              </div>
            </div>

            <div className="row">
              <div className="form-group col-6">
                <label>الفصل الدراسي</label>
                <select
                  name="class_id"
                  value={formData.class_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">اختر الفصل</option>
                  {teacherClasses && teacherClasses.map((classItem) => (
                    <option key={classItem.id} value={classItem.id}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group col-6">
                <label>المادة الدراسية</label>
                <select
                  name="subject_id"
                  value={formData.subject_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">اختر المادة</option>
                  {teacherSubjects && teacherSubjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row">
              <div className="form-group col-6">
                <label>اليوم</label>
                <input
                  type="date"
                  name="day"
                  value={formData.day}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group col-6">
                <label>الوقت</label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

                         <div className="form-group">
               <label>رابط الفيديو</label>
               <input
                 type="url"
                 name="video_link"
                 value={formData.video_link}
                 onChange={handleInputChange}
                 placeholder="أدخل رابط الفيديو"
               />
             </div>
             
             <div className="form-group">
               <label>نوع الفيديو</label>
               <select
                 name="video_type"
                 value={formData.video_type}
                 onChange={handleInputChange}
                 required
               >
                 <option value="offline">فيديو مسجل</option>
                 <option value="online">فيديو مباشر</option>
               </select>
             </div>
          </div>

          <div className="form-section">
            <h4>صورة الدرس</h4>
            <div className="form-group">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {previewImage && (
                <div className="image-preview">
                  <img src={previewImage} alt="معاينة" />
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h4>مميزات الدرس</h4>
            {Array.isArray(formData.features) && formData.features.map((feature, index) => (
              <div key={index} className="feature-row">
                <div className="form-group">
                  <label>العنوان (عربي)</label>
                  <input
                    type="text"
                    value={feature.title_ar}
                    onChange={(e) => handleFeatureChange(index, 'title_ar', e.target.value)}
                    placeholder="أدخل العنوان بالعربية"
                  />
                </div>

                <div className="form-group">
                  <label>العنوان (إنجليزي)</label>
                  <input
                    type="text"
                    value={feature.title_en}
                    onChange={(e) => handleFeatureChange(index, 'title_en', e.target.value)}
                    placeholder="أدخل العنوان بالإنجليزية"
                  />
                </div>

                {Array.isArray(formData.features) && formData.features.length > 1 && (
                  <button
                    type="button"
                    className="remove-feature-btn"
                    onClick={() => removeFeature(index)}
                  >
                    حذف
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="add-feature-btn"
              onClick={addFeature}
            >
              إضافة ميزة جديدة
            </button>
          </div>

          <div className="form-section">
            <h4>ملفات الدرس</h4>
            {Array.isArray(formData.files) && formData.files.map((fileItem, index) => (
              <div key={index} className="file-item">
                <div className="file-header">
                  {Array.isArray(formData.files) && formData.files.length > 1 && (
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={() => removeFile(index)}
                    >
                      حذف الملف
                    </button>
                  )}
                </div>

                <div className="file-input-section row">
                  <div className="form-group col-12 w-100">
                    <label>اختر الملف:</label>
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleFileTitleChange(index, 'file', file);
                        }
                      }}
                      className="file-input"
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                    />
                  </div>
                  <div className="file-titles row">
                    <div className="form-group col-6">
                      <label>اسم الملف بالعربية:</label>
                      <input
                        type="text"
                        value={fileItem.title_ar}
                        onChange={(e) => handleFileTitleChange(index, 'title_ar', e.target.value)}
                        placeholder="أدخل اسم الملف بالعربية"
                        className="file-title-input"
                      />
                    </div>
                    <div className="form-group col-6">
                      <label>اسم الملف بالإنجليزية:</label>
                      <input
                        type="text"
                        value={fileItem.title_en}
                        onChange={(e) => handleFileTitleChange(index, 'title_en', e.target.value)}
                        placeholder="أدخل اسم الملف بالإنجليزية"
                        className="file-title-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="add-file-btn"
              onClick={addFile}
            >
              + إضافة ملف جديد
            </button>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={isUpdating}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isUpdating}
            >
              {isUpdating ? 'جاري التحديث...' : 'تحديث الدرس'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditLessonModal;
